// modals/CalendarFactorModal.jsx
//
// "Edit Factors" modal for CalendarView. Edits the three config tables
// behind the calendar collection factor:
//   - calendar_factor_dom    (31 day-of-month factors — the payday curve)
//   - calendar_factor_dow    (7 day-of-week floors)
//   - calendar_factor_config (holiday multiplier)
//
// Resolution (read-only reminder shown in the modal):
//   factor(date) = MAX(day-of-month, day-of-week floor)
//                  × (holiday ? holiday_multiplier : 1)
//
// Saving applies live to the Compare projection — no calendar regenerate.

import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Snackbar, TextField, Typography,
} from "@mui/material";

import {
  getCalendarFactors, saveCalendarFactors,
} from "../../../services/forecast/forecastservices";
import { BLUE } from "../forecastShared";

const DOW = [
  { num: 1, label: "Mon" }, { num: 2, label: "Tue" }, { num: 3, label: "Wed" },
  { num: 4, label: "Thu" }, { num: 5, label: "Fri" }, { num: 6, label: "Sat" },
  { num: 7, label: "Sun" },
];

// Compact decimal input — text + inputMode so no spinners / wheel changes.
function FactorInput({ value, onChange, disabled, width = 64 }) {
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const shown = focused ? draft : (value == null ? "" : String(value));
  return (
    <TextField
      size="small" type="text" disabled={disabled}
      value={shown}
      onFocus={() => { setDraft(value == null ? "" : String(value)); setFocused(true); }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
          setDraft(raw);
          if (raw === "" || raw === ".") onChange(0);
          else { const n = Number(raw); if (!isNaN(n)) onChange(Math.max(0, n)); }
        }
      }}
      inputProps={{ inputMode: "decimal", style: { fontSize: 12, textAlign: "right", padding: "5px 8px" } }}
      sx={{ width }}
    />
  );
}

export default function CalendarFactorModal({ open, onClose, onSaved }) {
  const [domMap, setDomMap]       = useState({});   // day_of_month -> factor
  const [dowMap, setDowMap]       = useState({});   // day_of_week  -> floor
  const [holidayMult, setHoliday] = useState(0.5);
  const [loading, setLoading]     = useState(false);
  const [saving,  setSaving]      = useState(false);
  const [error,   setError]       = useState("");
  const [toast,   setToast]       = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError("");
    getCalendarFactors()
      .then((data) => {
        const dm = {}; (data?.dom || []).forEach(r => { dm[r.day_of_month] = Number(r.factor); });
        const dw = {}; (data?.dow || []).forEach(r => { dw[r.day_of_week]  = Number(r.floor_factor); });
        setDomMap(dm); setDowMap(dw);
        setHoliday(Number(data?.config?.holiday_multiplier ?? 0.5));
      })
      .catch((e) => setError(e?.response?.data?.error || e?.message || "Failed to load factors"))
      .finally(() => setLoading(false));
  }, [open]);

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      await saveCalendarFactors({
        dom: Array.from({ length: 31 }, (_, i) => ({ day_of_month: i + 1, factor: Number(domMap[i + 1] ?? 0) })),
        dow: DOW.map(d => ({ day_of_week: d.num, floor_factor: Number(dowMap[d.num] ?? 0) })),
        holiday_multiplier: Number(holidayMult),
      });
      setToast({ open: true, message: "Calendar factors saved", severity: "success" });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to save");
      setToast({ open: true, message: "Save failed", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 500, pb: 0.5 }}>
          Edit calendar factors
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
            factor = MAX(day-of-month, day-of-week floor) × (holiday ? holiday multiplier : 1).
            Applies live to the Compare projection — no regenerate needed.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
              <CircularProgress size={18} /> Loading…
            </Box>
          ) : (
            <>
              {/* Day-of-month curve */}
              <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase",
                letterSpacing: "0.05em", mb: 1 }}>
                Day-of-month curve (payday cycle)
              </Typography>
              <Box sx={{
                display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 1, mb: 3,
              }}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <Box key={d} sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Typography sx={{ fontSize: 10, color: "text.disabled", textAlign: "center" }}>
                      {d}
                    </Typography>
                    <FactorInput
                      value={domMap[d]}
                      onChange={(v) => setDomMap(m => ({ ...m, [d]: v }))}
                      disabled={saving}
                      width="100%"
                    />
                  </Box>
                ))}
              </Box>

              {/* Day-of-week floors */}
              <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase",
                letterSpacing: "0.05em", mb: 1 }}>
                Day-of-week floors <Box component="span" sx={{ textTransform: "none", color: "text.disabled" }}>
                  (Mon–Thu usually 0; floors only lift otherwise-low days)
                </Box>
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
                {DOW.map((d) => (
                  <Box key={d.num} sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Typography sx={{ fontSize: 10, color: "text.disabled", textAlign: "center" }}>
                      {d.label}
                    </Typography>
                    <FactorInput
                      value={dowMap[d.num]}
                      onChange={(v) => setDowMap(m => ({ ...m, [d.num]: v }))}
                      disabled={saving}
                      width={64}
                    />
                  </Box>
                ))}
              </Box>

              {/* Holiday multiplier */}
              <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase",
                letterSpacing: "0.05em", mb: 1 }}>
                Holiday multiplier <Box component="span" sx={{ textTransform: "none", color: "text.disabled" }}>
                  (cuts any day — e.g. 0.5 = collect 50% on holidays)
                </Box>
              </Typography>
              <FactorInput value={holidayMult} onChange={setHoliday} disabled={saving} width={90} />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontSize: 12 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            variant="contained"
            sx={{
              textTransform: "none", fontSize: 12, borderRadius: "20px",
              background: BLUE, boxShadow: "none",
              "&:hover": { background: "#2A6FB0", boxShadow: "none" },
            }}
          >
            {saving ? "Saving…" : "Save factors"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} sx={{ fontSize: 13 }}
          onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}